<script setup lang="ts">
import { ref, inject, computed, h, Ref, onMounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import {
  AIR_ONLY,
  CRUISE,
  HOTEL_ONLY,
  PACKAGE,
  BOOKING_FORM,
  QUOTE_STS,
  RQ_TO_BOOK,
  DEPT,
  DEPT_STS,
  NEW,
  ASSIGNED_TO,
  UNASSIGNED,
  TOUR,
  LANG_FRENCH,
  INCENTIVES,
  QT_COORDINATOR,
  QT_AIR,
  QT_CRUISE,
  QT_INCENTIVE,
  INCENTIVE_GROUP_TYPE
} from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useQuoteAssignStore } from '@/store/QuoteAssignStore';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useBookComposable } from '@/components/book/useBookComposable';
import { useModalComposable, useViewComposable } from '@/views/composable';
import { useTermsAndConditionsStore } from '@/store/TermsAndConditionsStore';
import { useToast } from 'primevue/usetoast';
import Accordion from 'primevue/accordion';
import AccordionContent from 'primevue/accordioncontent';
import AccordionHeader from 'primevue/accordionheader';
import AccordionPanel from 'primevue/accordionpanel';
import Button from 'primevue/button';
import AirOption from './book/AirOption.vue';
import CruiseOption from './book/CruiseOption.vue';
import HotelOption from './book/HotelOption.vue';
import PackageOption from './book/PackageOption.vue';
import TourOption from '@/components/book/TourOption.vue';

const dialogRef = inject('dialogRef') as Ref;
const isSave = dialogRef?.value?.options.emits.isSave;
const status = dialogRef?.value?.options.props.status;
const priorityStatus = dialogRef?.value?.options.props.priorityStatus;
const isManagement = dialogRef?.value?.options.props.isManagement;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const adminSetupStore = useAdminSetupStore();
const bookingQuoteStore = useBookingQuoteStore();
const { removeHtmlTags } = useViewComposable();
const { showMinNotification } = useModalComposable();
const submitWorksheetStore = useSubmitWorksheetStore();
const termsAndConditionsStore = useTermsAndConditionsStore();
const quoteAssignStore = useQuoteAssignStore();
const { validateBookingHotelOptions, validateBookingFlight, validateBookingCruise, validateBookingPackage, validateBookingMdt } = useBookComposable();

const products = ref();
const termAndCondition = ref(null);
const validateBooking = ref(true);
const isDisableSubmit = ref(true);
const activePanel = ref();

const quote = computed(() => bookingQuoteStore.quote);
const minimumConfigValue = computed(() => adminSetupStore.minimumConfig);

const isAirInputNumOfSeatsNotEmpty = computed(() => {
  if (quote.value.qType !== AIR_ONLY) {
    return true;
  }

  return quote.value.airs.some((airOption) => {
    return airOption.price.some((priceItem) => priceItem.adtNo || priceItem.chdNo);
  });
});

const getBookingTermsAndConditions = async () => {
  const qType = quote.value.gType.includes('Incentive') ? INCENTIVES : quote.value.qType;
  const response = await termsAndConditionsStore.getData(BOOKING_FORM, qType);
  termAndCondition.value = response;
};

const renderBookTermHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-terms', innerHTML: termAndCondition.value.termsFr });
  } else {
    return h('div', { class: 'english-terms', innerHTML: termAndCondition.value.terms });
  }
};

const renderBookIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'french-intro', innerHTML: termAndCondition.value.introFr });
  } else {
    return h('div', { class: 'english-intro', innerHTML: termAndCondition.value.intro });
  }
};

const initProducts = () => {
  switch (quote.value.qType) {
    case PACKAGE:
      products.value = quote.value.packages;
      break;
    case HOTEL_ONLY:
      products.value = quote.value.hotels;
      break;
    case AIR_ONLY:
      products.value = quote.value.airs;
      break;
    case CRUISE:
      products.value = quote.value.cruises;
      break;
    default:
      break;
  }
};

const handleSave = async () => {
  try {
    const payload = {};
    switch (quote.value.qType) {
      case PACKAGE:
        payload.packages = quote.value.packages;
        break;
      case HOTEL_ONLY:
        payload.hotels = quote.value.hotels;
        break;
      case AIR_ONLY:
        payload.airs = quote.value.airs;
        break;
      case CRUISE:
        payload.cruises = quote.value.cruises;
        break;
      case TOUR:
        payload.multiDayTour = quote.value.multiDayTour;
        break;
    }
    payload.qType = quote.value.qType;
    payload.agencyPhone = quote.value.agencyPhone;
    payload.bookedDate = new Date();
    payload.expires = quote.value.expires;
    payload.linkedQuotes = quote.value.linkedQuotes;
    await submitWorksheetStore.handleUpdateWorkSheet(payload, quote.value.sk);
    isSave(true);
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    isSave(false);
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

const handleAssignRoleBook = (data: any) => {
  if ((data.groupType && data.groupType !== INCENTIVE_GROUP_TYPE) || (data.gType && data.gType !== INCENTIVE_GROUP_TYPE)) {
    if (data.sk.includes('IM') || data.sk.includes('IQ') || data.sk.includes('HM')) {
      return QT_COORDINATOR;
    }
    if (data.sk.includes('AM')) {
      return QT_AIR;
    }
    if (data.sk.includes('CM')) {
      return QT_CRUISE;
    }
  }
  return QT_INCENTIVE;
};

const handleSubmitToBook = async () => {
  try {
    const payload = {};
    let errorMinMessage = '';
    switch (quote.value.qType) {
      case PACKAGE:
        payload.packages = quote.value.packages;
        errorMinMessage = t('advance_quote.form.min_pax_package_book', { minPax: minimumConfigValue.value.packageConfig.minPax, minRoom: minimumConfigValue.value.packageConfig.minRoomsCabins });
        validateBooking.value = validateBookingPackage(quote.value.packages, minimumConfigValue.value.packageConfig);
        break;
      case HOTEL_ONLY:
        payload.hotels = quote.value.hotels;
        errorMinMessage = t('advance_quote.form.min_pax_package_book', { minPax: minimumConfigValue.value.hotelConfig.minPax, minRoom: minimumConfigValue.value.hotelConfig.minRoomsCabins });
        validateBooking.value = validateBookingHotelOptions(quote.value.hotels, minimumConfigValue.value.hotelConfig);
        break;
      case AIR_ONLY:
        payload.airs = quote.value.airs;
        validateBooking.value = validateBookingFlight(quote.value.airs, minimumConfigValue.value.airConfig);
        errorMinMessage = t('advance_quote.form.min_pax_air_book', { minPax: minimumConfigValue.value.airConfig.minPax });
        break;
      case CRUISE:
        payload.cruises = quote.value.cruises;
        validateBooking.value = validateBookingCruise(quote.value.cruises, minimumConfigValue.value.cruiseConfig);
        errorMinMessage = t('advance_quote.form.min_pax_cruise_book', { minPax: minimumConfigValue.value.cruiseConfig.minPax, minRoom: minimumConfigValue.value.cruiseConfig.minRoomsCabins });
        break;
      case TOUR:
        payload.multiDayTour = quote.value.multiDayTour;
        errorMinMessage = t('advance_quote.form.min_pax_package_book', { minPax: minimumConfigValue.value.tourConfig.minPax, minRoom: minimumConfigValue.value.tourConfig.minRoomsCabins });
        validateBooking.value = validateBookingMdt(quote.value.multiDayTour, minimumConfigValue.value.tourConfig);
        break;
    }
    payload.qType = quote.value.qType;
    payload.bookedDate = new Date();
    payload.expires = quote.value.expires;
    payload.agencyPhone = quote.value.agencyPhone;
    payload.linkedQuotes = quote.value.linkedQuotes;

    if (!quote.value.isReplicate && validateBooking.value) {
      showMinNotification(errorMinMessage);
    } else {
      const role = handleAssignRoleBook(quote.value);
      await submitWorksheetStore.handleUpdateWorkSheet(payload, quote.value.sk);
      await Promise.all([
        quoteAssignStore.handleAssign({ assignType: QUOTE_STS, value: RQ_TO_BOOK }, quote.value.sk),
        quoteAssignStore.handleAssign({ assignType: DEPT, value: role }, quote.value.sk),
        quoteAssignStore.handleAssign({ assignType: DEPT_STS, value: NEW }, quote.value.sk),
        quoteAssignStore.handleAssign({ assignType: ASSIGNED_TO, value: UNASSIGNED }, quote.value.sk)
      ]);
      isSave(true);
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    }
  } catch (error) {
    isSave(false);
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

const checkIsHasData = (data, currentLanguage) => {
  if (currentLanguage === 'fr') {
    return removeHtmlTags(data?.introFr);
  } else {
    return removeHtmlTags(data?.intro);
  }
};

const openClickSubmit = (event: boolean) => {
  isDisableSubmit.value = event;
};

initProducts();
getBookingTermsAndConditions();
onMounted(() => {
  adminSetupStore.getMinimumConfig();
});
</script>

<template>
  <div class="book-form">
    <h2>{{ $t('booking.rooming_title') }} {{ quote?.sk }}</h2>
    <div class="book-title">
      <renderBookIntroHtml v-if="termAndCondition"></renderBookIntroHtml>
    </div>
    <hr />
    <div class="grid">
      <div class="col button-wrapper">
        <Button v-if="status !== 'BOOKED' && authorizations?.my_quote?.book_edit" class="bg-primary mr-3 hidden" :label="$t('booking.save_occupancy')" @click="handleSave()" />
        <Button
          id="submit-to-book-id"
          v-if="status !== 'BOOKED' && authorizations?.my_quote?.book_edit"
          class="bg-primary mr-3"
          :label="$t('booking.submit_to_book')"
          :disabled="(isDisableSubmit && [PACKAGE, HOTEL_ONLY, CRUISE, TOUR].includes(quote?.qType)) || !isAirInputNumOfSeatsNotEmpty"
          @click="handleSubmitToBook()"
        />
      </div>
    </div>
    <div class="grid">
      <div class="col-12 bg-blue-5">
        <div class="card">
          <div v-if="quote?.qType === PACKAGE"><PackageOption :dataProducts="products" :isManagement="isManagement" :priorityStatus="priorityStatus" @openClickSubmit="openClickSubmit" /></div>
          <div v-if="quote?.qType === HOTEL_ONLY"><HotelOption :dataProducts="products" :isManagement="isManagement" :priorityStatus="priorityStatus" @openClickSubmit="openClickSubmit" /></div>
          <div v-if="quote?.qType === AIR_ONLY"><AirOption :dataProducts="products" :isManagement="isManagement" :priorityStatus="priorityStatus" /></div>
          <div v-if="quote?.qType === CRUISE"><CruiseOption :dataProducts="products" :isManagement="isManagement" :priorityStatus="priorityStatus" @openClickSubmit="openClickSubmit" /></div>
          <div v-if="quote?.qType === TOUR"><TourOption :isManagement="isManagement" :priorityStatus="priorityStatus" @openClickSubmit="openClickSubmit" /></div>
        </div>
      </div>
    </div>
    <Accordion v-model:value="activePanel" v-if="checkIsHasData(termAndCondition, currentLanguage)" class="acvShadow">
      <AccordionPanel value="0">
        <AccordionHeader>
          <div class="h6style m-0">{{ $t('booking.terms_and_conditions') }}</div>
          <div class="absolute px-3 flex align-items-center bg-red-500 expand-icon">
            <i v-if="activePanel != 0" class="pi pi-plus-circle text-4xl text-white-alpha-90"></i>
            <i v-else class="pi pi-minus-circle text-4xl text-white-alpha-90"></i>
          </div>
        </AccordionHeader>
        <AccordionContent>
          <div class="book-term">
            <renderBookTermHtml v-if="termAndCondition"></renderBookTermHtml>
          </div>
        </AccordionContent>
      </AccordionPanel>
    </Accordion>
  </div>
</template>
